<template>
  <div>
    <div v-if="event?.dataModels?.length > 0" class="w-100">
      <div class="d-flex mb-3">
        <h4 class="data-model-title">
          Command
        </h4>
      </div>
      <div
        v-for="(dataField, index) in dataFieldsWriteModel"
        :key="`${index}-write`"
        class="field-box"
      >
        <div class="field-box-label">
          <v-tooltip v-if="dataField.reference" location="bottom" open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="small" icon="mdi-link" color="#1F2833" />
            </template>
            <span>Reference to entity</span>
          </v-tooltip>
          {{ dataField.name }}
          {{
            !dataField.reference && !!dataField.matchingField?.relatedEntity ?
              (dataField.matchingField.cardinality === 'one-to-many' ? '(Collection)' : '(Embedded object)') : ''
          }}
        </div>
        <div class="field-box-content" v-if="!dataField.reference">
          <div v-if="!!dataField.matchingField && !!dataField.matchingField.relatedEntity">
            <div v-if="dataField.matchingField.cardinality === 'one-to-many'" >
              <div class="embedded-object-table-container mb-1">
                <table class="w-100">
                  <thead>
                    <tr>
                      <th v-for="field in dataField.matchingField.relatedEntity?.dataFields || []" :key="field.name">
                        {{ field.name }}
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(data, dataIndex) in dataField.matchingField.relatedEntity?.dataFields?.[0]?.exampleData || []"
                      :key="data"
                    >
                      <td v-for="field in dataField.matchingField.relatedEntity?.dataFields || []" :key="field.name">
                        {{ field?.exampleData?.[dataIndex] || '-' }}
                      </td>
                      <td>
                        <div class="d-flex align-center ga-1">
                          <v-icon size="14" color="grey" icon="mdi-pencil" class="cursor-pointer" />
                          <v-icon size="14" color="grey" icon="mdi-delete" class="cursor-pointer" />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <v-btn
                class="add-item-btn"
                variant="outlined"
                size="small"
                @click="() => {}"
                prepend-icon="mdi-plus-circle-outline"
              >
                Add Item
              </v-btn>
            </div>
            <div v-else>
              <template v-for="field in dataField.matchingField.relatedEntity?.dataFields || []" :key="field.name">
                <div class="embedded-object-field d-flex justify-space-between align-center ga-2 py-1 px-2">
                  <div class="embedded-object-field-label text-truncate">{{ field.name }}:</div>
                  <div class="embedded-object-field-value text-truncate">{{ field?.exampleData?.[0] || '-' }}</div>
                  <div>
                    <v-icon size="14" color="grey" icon="mdi-pencil" class="cursor-pointer" />
                  </div>
                </div>
              </template>
            </div>
          </div>
          <span v-else>
            {{ dataField.exampleData || '-' }}
          </span>
        </div>
        <v-menu class="data-field-options-menu" :close-on-content-click="false" v-else>
          <template v-slot:activator="{ props }">
            <div class="d-flex align-items-center" v-bind="props">
              <div class="field-box-content d-inline-flex align-center flex-grow-1">
                {{ dataField.exampleData || '-' }}
              </div>
              <v-icon icon="mdi-menu-down" color="#1F2833" />
            </div>
          </template>
          <v-list density="compact">
            <v-list-item v-for="(option) in dataField.options" :key="option">
              <v-list-item-title class="d-flex align-center ga-2 cursor-pointer">
                <v-checkbox v-if="dataField.cardinality === 'one-to-many'" readonly hide-details />
                {{ option }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
    <div v-else>
      {{ event ? 'No Command defined' : 'Select an event to view the command' }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { cardHelpers } from '@/mixins/cardHelpers'
import useDomainModel from '@/composables/useDomainModel'

const { event } = defineProps({ event: Object })

const { getMatchingFieldInEntity } = useDomainModel()
const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const rootEntity = computed(() => {
  const entityCard = cardHelpers.methods.getEntityCard(event)

  return entityCard?.entityId
    ? workflow.value.dataModels?.find((entity) => entity.id === entityCard.entityId)
    : null
})

const dataFieldsWriteModel = computed(() => {
  const writeModel = event.dataModels?.find((model) => model.type === 'Event Story')

  if (!writeModel) {
    return []
  }

  const filteredDataFields = writeModel.dataFields.filter((field) => !field.hideInForm)

  return filteredDataFields.map((field) => {
    const matchingField = getMatchingFieldInEntity(rootEntity.value, field.name)
    const relatedEntity = matchingField?.relatedEntityId
      ? workflow.value.dataModels?.find((entity) => entity.id === matchingField.relatedEntityId)
      : null

    let exampleData = ''
    const options = []

    if (matchingField) {
      if (matchingField.reference && relatedEntity) {
        relatedEntity.dataFields.forEach((field) => {
          if (field.primaryKey) {
            exampleData = field.exampleData?.find((item) => !!item)
          }

          (field.exampleData || []).forEach((item, dataIndex) => {
            if (!options[dataIndex]) {
              options[dataIndex] = item || ''
            } else {
              options[dataIndex] += item ? ` — ${item}` : ''
            }
          })
        })

        if (!exampleData) {
          exampleData = firstExistingExampleData(relatedEntity)
        }
      } else {
        exampleData = matchingField.exampleData?.[0] || ''
      }
    }

    const subfieldNames = (field.dataFields ?? []).map(subfield => subfield.name)
    const matchingFieldRelatedEntityFields = matchingField?.relatedEntity?.dataFields?.filter(item =>
      subfieldNames.includes(item.name)
    )

    return {
      ...field,
      cardinality: field?.cardinality || 'one-to-one',
      matchingField: matchingField && matchingField.relatedEntity
        ? { ...matchingField, relatedEntity: { ...matchingField.relatedEntity, dataFields: matchingFieldRelatedEntityFields } }
        : null,
      reference: !!matchingField?.reference,
      options: options.filter(Boolean),
      exampleData
    }
  })
})

const firstExistingExampleData = (entity) => {
  if (!entity.dataFields?.length) {
    return null
  }

  for (const field of entity.dataFields) {
    for (const item of (field.exampleData || [])) {
      if (item) {
        return item
      }
    }
  }
}
</script>

<style scoped>
.data-model-title {
  border-radius: 4px;
  background: #F5F2F0;
  padding: 5px 8px;
  align-items: flex-start;
}

.field-box {
  padding: 8px 16px;
  border: 1px solid #868E99;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.03);
  margin-bottom: 22px;

  .field-box-label {
    font-size: 12px;
    color: grey;
    margin-bottom: 4px;
  }
}

:deep(.v-checkbox) {
  .v-input__control {
    .v-selection-control {
      min-height: 0;

      .v-selection-control__wrapper {
        width: 21px;
        height: 21px;
      }
    }
  }
}

.data-field-options-menu {
  .v-overlay__content {
    .v-list-item {
      &:hover {
        background-color: rgba(0, 0, 0, 0.04) !important;
      }
    }
  }
}

.embedded-object-field {
  background: #ffffff;
  border-radius: 6px;

  .embedded-object-field-label {
    width: 90px;
  }

  .embedded-object-field-value {
    flex-grow: 1;
  }

  &:not(:last-child) {
    margin-bottom: 4px;
  }
}

.embedded-object-table-container {
  overflow: auto;
  background: #ffffff;
  border-radius: 6px;

  table {
    border-collapse: collapse;

    tr {
      th {
        height: auto !important;
        color: #8f8f8f;
        padding: 8px 8px 0 8px;
        text-align: left;
        font-size: 12px;
        font-weight: 400;
        border-bottom: 1px solid #d5d5d5;
        white-space: nowrap;
      }

      td {
        padding: 8px;
        font-size: 13px;
        font-weight: 400;
        height: auto !important;
        white-space: nowrap;

        &:focus-within, &:focus {
          outline: none;
        }
      }
    }
  }
}

:deep(.add-item-btn) {
  &.v-btn.v-btn--size-small.v-btn--variant-outlined {
    padding: 2px 8px 2px 4px !important;
    height: 24px !important;

    .v-btn__content {
      font-size: 12px;
    }

    .v-btn__prepend {
      margin-inline: 0;

      .v-icon {
        font-size: 15px;
      }
    }
  }
}
</style>
